import constants from '@Common/Core/Constant';
import Guid from '@Common/Core/Guid';
import { EntityFilter } from '@Common/Core/InstanceFactory/Instances.Correcter';
import { properties } from '@Common/Core/Properties';
import { dimensionFilterValue, filterValue } from '@Common/Entities/FilterEntities';
import { EnumValue, FilterType, TypeKind } from '@Common/Enums';
import { DataTimeIntervalHelper } from '@Common/Helpers/DataTimeIntervalHelper';
import documentHelper from '@Common/Helpers/DocumentHelper';

import timeHelper from '@Common/Helpers/TimeHelper';
import { DimensionFilter, DimensionFilterValues, GroupFilter, Process, ProcessConfiguration, RecordData, RecordSeries } from '@InstanceFactory';
import { Base } from '@View/Components/Base';
import { processChartsHelper } from '@View/Helpers/Charts/ProcessChartsHelper';

export namespace filterHelper {
    export function getGroupName(groupName: string, viewId: Guid, projectId: Guid): string {
        if (isCurrentGroup(groupName, projectId)) {
            return groupName + viewId;
        }
        return groupName;
    }

    export function getLocalGroupsName(viewId: string): string {
        return constants.currentViewsGroupId + viewId;
    }

    export function getFilterGroup(group: string, parentId?: string, projectId?: Guid): FilterGroup {
        return new FilterGroup(group, getGroupName(group, parentId, projectId));
    }

    export function getAffectedDriverGroups(affectedDriverGroups: FilterGroup[], parentIds: string[], projectId: Guid): FilterGroup[] {
        const resultGroups = affectedDriverGroups.slice();
        const currentViewGroups = _.filter(affectedDriverGroups, item => isCurrentGroup(item.groupName, projectId));
        for (const parentId of parentIds) {
            for (const currentViewGroup of currentViewGroups) {
                const filterGroup = getFilterGroup(currentViewGroup.groupName, parentId, projectId);
                resultGroups.push(filterGroup);
            }
        }
        return _.uniqBy(resultGroups, g => g.groupName + '|' + g.groupValue);
    }

    export function isCurrentGroup(groupName: string, projectId: Guid): boolean {
        if (groupName == null) {
            return false;
        }
        const { isLocal } = _.find(documentHelper.getAllDriverGroups(projectId), { name: groupName }) || {
            isLocal: isLocalGroupName(groupName)
        };
        return isLocal;
    }

    function isLocalGroupName(groupName: string): boolean {
        const isCurrentView = groupName.indexOf(constants.currentViewGroupName) !== -1;
        const endWithGuid = Guid.isGuid(groupName.slice(-Guid.empty.length));
        return !(properties.isPrint && endWithGuid) && isCurrentView;
    }

    export function mergeGroupFiltersList(groupsFilters1: GroupFilter[], groupsFilters2: GroupFilter[]): GroupFilter[] {
        for (const filter2 of groupsFilters2) {
            const filter1 = _.find(groupsFilters1, item => item.groupName === filter2.groupName);
            if (filter1) {
                groupsFilters1.remove(filter1);
                groupsFilters1.push(mergeGroupFilters(filter1, filter2));
            } else {
                groupsFilters1.push(filter2);
            }
        }
        return groupsFilters1;
    }

    export function mergeGroupFilters(groupFilter1: GroupFilter, groupFilter2: GroupFilter): GroupFilter {
        const groupName = groupFilter1.groupName || groupFilter2.groupName;
        const dimensionValues = mergeFilters(groupFilter1.dimensionValues, groupFilter2.dimensionValues);
        const entityFilter = mergeEntityFilters(groupFilter1.entityFilter, groupFilter2.entityFilter);
        const parameterValues = mergeFilters(groupFilter1.parameterValues, groupFilter2.parameterValues);
        const processFilters = mergeProcessFilters(groupFilter1.processFilters, groupFilter2.processFilters);
        const dimensionConditions = mergeFilters(groupFilter1.dimensionConditions, groupFilter2.dimensionConditions);
        const recordsData = mergeRecordsData(groupFilter1.recordsData, groupFilter2.recordsData);
        const dimensionAllValuesExcluded = mergeDimensionAllValuesExcluded(
            groupFilter1.dimensionAllValuesExcluded,
            groupFilter2.dimensionAllValuesExcluded
        );
        const seriesId = groupFilter1.seriesId || groupFilter2.seriesId;
        return groupFilter({
            groupName,
            dimensionValues,
            entityFilter,
            parameterValues,
            dimensionConditions,
            recordsData,
            seriesId,
            processFilters,
            dimensionAllValuesExcluded
        });
    }

    export function mergeDimensionAllValuesExcluded(dimensionAllValuesExcluded1: string[], dimensionAllValuesExcluded2: string[]): string[] {
        if (dimensionAllValuesExcluded1 == null && dimensionAllValuesExcluded2 == null) {
            return null;
        } else if (dimensionAllValuesExcluded1 == null) {
            return dimensionAllValuesExcluded2.slice();
        } else if (dimensionAllValuesExcluded2 == null) {
            return dimensionAllValuesExcluded1.slice();
        }
        const dimensionAllValuesExcluded = dimensionAllValuesExcluded1.slice();
        for (const dimension of dimensionAllValuesExcluded2) {
            if (!dimensionAllValuesExcluded.contains(dimension)) {
                dimensionAllValuesExcluded.push(dimension);
            }
        }

        return dimensionAllValuesExcluded;
    }

    export function mergeProcessFilters(newProcessFilters: any[], existingProcessFilters: any[]): any[] {
        if (newProcessFilters && newProcessFilters.length && newProcessFilters[0].id === constants.noFilterId) {
            return [];
        }
        return _.chain(newProcessFilters)
            .unionBy(existingProcessFilters, filter => filter.componentId)
            .filter(filter => filter)
            .value();
    }

    export function mergeEntityFilters(entityFilter1: EntityFilter, entityFilter2: EntityFilter): EntityFilter {
        let entityFilter: EntityFilter = entityFilter1 || entityFilter2 ? ({} as any) : null;
        if (entityFilter) {
            entityFilter.metricId =
                entityFilter2 && entityFilter2.metricId != null && entityFilter2.metricId !== constants.guidEmpty
                    ? entityFilter2.metricId
                    : entityFilter1
                    ? entityFilter1.metricId
                    : constants.guidEmpty;
            entityFilter.recordId =
                entityFilter2 && entityFilter2.recordId != null && entityFilter2.recordId !== constants.guidEmpty
                    ? entityFilter2.recordId
                    : entityFilter1
                    ? entityFilter1.recordId
                    : constants.guidEmpty;
        } else {
            entityFilter = {
                metricId: constants.guidEmpty,
                recordId: constants.guidEmpty
            };
        }
        return entityFilter;
    }

    export function mergeRecordsData(recordsData1: RecordData[], recordsData2: RecordData[]): RecordData[] {
        if (recordsData1 == null && recordsData2 == null) {
            return null;
        } else if (recordsData1 == null) {
            return recordsData2.slice();
        } else if (recordsData2 == null) {
            return recordsData1.slice();
        }
        return _.unionBy(recordsData1, recordsData2, rd => rd.recordId);
    }

    export function mergeFilters(filterValues1: DimensionFilterValues[], filterValues2: DimensionFilterValues[]): DimensionFilterValues[] {
        if (filterValues1 == null && filterValues2 == null) {
            return null;
        } else if (filterValues1 == null) {
            return filterValues2.slice();
        } else if (filterValues2 == null) {
            return filterValues1.slice();
        }
        const filterValues = filterValues1.slice();
        for (const filterValue2 of filterValues2) {
            const existedFilter = _.find(filterValues, item => item.id == filterValue2.id);
            if (existedFilter != null) {
                const filter = mergeDimensionFilterValues(existedFilter, filterValue2);
                filterValues.splice($.inArray(existedFilter, filterValues), 1);
                filterValues.push(filter);
            } else {
                filterValues.push(filterValue2);
            }
        }

        return filterValues;
    }

    export function mergeDimensionFilterValues(filterValue1: DimensionFilterValues, filterValue2: DimensionFilterValues): DimensionFilterValues {
        let values;
        const canMerge =
            _.includes([FilterType.whereNotIn, FilterType.whereIn], filterValue1.filterType) ||
            _.includes([FilterType.whereNotIn, FilterType.whereIn], filterValue2.filterType);
        if (!canMerge) {
            return filterValue1;
        }
        if (filterValue1.filterType === FilterType.whereNotIn && filterValue2.filterType === FilterType.whereNotIn) {
            const union = _.unionBy(filterValue2.values, filterValue1.values, value => value.id);
            values = _.map(union, value => filterValue(value.id, value.value));
        } else if (filterValue1.filterType === FilterType.whereNotIn || filterValue2.filterType === FilterType.whereNotIn) {
            const firstFilterValue = filterValue1.filterType === FilterType.whereNotIn ? filterValue2 : filterValue1;
            const secondFilterValue = filterValue1.filterType === FilterType.whereNotIn ? filterValue1 : filterValue2;

            const difference = _.differenceBy(firstFilterValue.values, secondFilterValue.values, value => value.id);
            values = _.map(difference, value => filterValue(value.id, value.value));
        } else {
            if (timeHelper.isTimeDrillDown(filterValue1.id) || filterValue1.id === constants.dateTimeFilterName) {
                const interval1 = timeHelper.unionIntervals(_.map(filterValue1.values, value => DataTimeIntervalHelper.parse(value.id)));
                const interval2 = timeHelper.unionIntervals(_.map(filterValue2.values, value => DataTimeIntervalHelper.parse(value.id)));
                const timeIntervals = [];
                if (interval1) {
                    timeIntervals.push(interval1);
                }
                if (interval2) {
                    timeIntervals.push(interval2);
                }
                const resultInterval = timeHelper.intersectIntervals(timeIntervals);
                values = resultInterval ? [filterValue(DataTimeIntervalHelper.toString(resultInterval))] : filterValue1.values;
            } else {
                const intersection = _.intersectionBy(filterValue2.values, filterValue1.values, value => value.id);
                values = _.map(intersection, value => filterValue(value.id, value.value));
            }
        }

        const filterType =
            filterValue1.filterType === FilterType.whereIn || filterValue2.filterType === FilterType.whereIn
                ? FilterType.whereIn
                : FilterType.whereNotIn;

        return dimensionFilterValue(filterValue1.id, values, filterType);
    }

    export function isDriverGroup(component: Base, groups: string[]): boolean {
        const parentIds = component.getParentIds();
        return (
            groups == null ||
            groups.length === 0 ||
            (groups.length === 1 && !groups[0]) ||
            _.includes(groups, component.getOutgoingGroupName()) ||
            _.some(parentIds, parentId => _.includes(groups, filterHelper.getLocalGroupsName(parentId)))
        );
    }

    export function processFilterToDimensionFilter(
        processFilters: (ProcessConfiguration & { id?: string })[],
        projectId: Guid
    ): DimensionFilterValues[] {
        return _(processFilters)
            .filter(value => value.id !== constants.noFilterId)
            .map(value => {
                let pathFilterValue;
                const process = documentHelper.getProcess(projectId, value.process);

                if (value.selectionOptions.subPath) {
                    pathFilterValue = filterValue(
                        value.process.id,
                        value.process.name + '; ' + processSubPathToString(value.selectionOptions.subPath, process.originalProcess)
                    );
                } else if (value.selectionOptions.paths) {
                    pathFilterValue = filterValue(
                        value.process.id,
                        value.process.name +
                            '; ' +
                            _.map(value.selectionOptions.paths as any, (path: number[]) =>
                                processSubPathToString(path, process.originalProcess)
                            ).join(', ')
                    );
                } else if (value.selectionOptions.step != null || value.selectionOptions.transition) {
                    const stepIndex =
                        value.selectionOptions.step != null
                            ? value.selectionOptions.step
                            : value.selectionOptions.transition.from != null
                            ? value.selectionOptions.transition.from
                            : value.selectionOptions.transition.to;
                    const filterString =
                        value.selectionOptions.step != null
                            ? strings.globalFilters.processFilterQueued
                            : value.selectionOptions.transition.from != null
                            ? strings.globalFilters.processFilterOutcoming
                            : strings.globalFilters.processFilterIncoming;
                    const step = _.find(process.originalProcess.steps, s => s.number === stepIndex) || {
                        title: stepIndex
                    };
                    const stepName = processChartsHelper.getStepTitle(step as any);
                    pathFilterValue = filterValue(value.process.id, value.process.name + '; ' + filterString.format(stepName));
                } else {
                    // Default
                    pathFilterValue = filterValue(value.process.id);
                }

                return dimensionFilterValue(constants.processPathFilterName, [pathFilterValue]);
            })
            .value();
    }

    export function processSubPathToString(subPath: number[], process: Process): string {
        return _.map(subPath, index => `${processChartsHelper.getStepNameByIndex(index, process)}`).join('—');
    }

    export function dimensionFilterToString(dimensionFilter: DimensionFilter): string {
        return _(dimensionFilter.dimensionValues)
            .filter(filter => !timeHelper.isTimeDrillDown(filter.id) && filter.id !== constants.dateTimeFilterName)
            .map(filter => {
                const filterTypeId = filter.filterType ? filter.filterType : FilterType.whereIn;
                const filterType = FilterType.getValue(filterTypeId);
                const values = _.map(filter.values, value => value.value).join(', ');
                return `"${filter.id}" ${filterType.name} (${values})`;
            })
            .join('; ');
    }

    export function seriesFilterToString(series: RecordSeries[]): string {
        return series
            .filter(s => {
                const filterItem = s.dataDefinition.filterItem;
                return filterItem && filterItem.operatorType !== FilterType.unknown && _.size(filterItem.values) > 0;
            })
            .map(s => {
                const filterItem = s.dataDefinition.filterItem;
                const operatorName = FilterType.getValue(filterItem.operatorType);
                return '{0} {1} {2}'.format(s.fieldPath, operatorName.name, filterItem.values.join(','));
            })
            .join('; ');
    }

    export function getFilterTypes(typeKind: TypeKind, emptyItem?: boolean): EnumValue<FilterType>[] {
        const filterTypes = [...(getFilterTypesInner(typeKind) || []), FilterType.isNull, FilterType.isNotNull];

        if (emptyItem) {
            filterTypes.unshift(FilterType.unknown);
        }

        return _.cloneDeep(FilterType.getValues(...filterTypes));
    }

    function getFilterTypesInner(typeKind: TypeKind): FilterType[] {
        switch (typeKind) {
            case TypeKind.string:
            case TypeKind.text:
                return [
                    FilterType.starts,
                    FilterType.ends,
                    FilterType.equal,
                    FilterType.notEqual,
                    FilterType.includes,
                    FilterType.like,
                    FilterType.whereIn,
                    FilterType.whereNotIn
                ];
            case TypeKind.dateTime:
                return [
                    FilterType.equal,
                    FilterType.less,
                    FilterType.lessOrEqual,
                    FilterType.greater,
                    FilterType.greaterOrEqual,
                    FilterType.range,
                    FilterType.between
                ];
            case TypeKind.float:
            case TypeKind.int:
                return [
                    FilterType.equal,
                    FilterType.notEqual,
                    FilterType.less,
                    FilterType.lessOrEqual,
                    FilterType.greater,
                    FilterType.greaterOrEqual,
                    FilterType.between
                ];
            case TypeKind.bool:
            case TypeKind.guid:
                return [FilterType.equal, FilterType.notEqual];
        }
    }
}

export class FilterGroup {
    constructor(public readonly groupName: string, public readonly groupValue: string = groupName) {}
}

export function groupFilter({
    groupName = '',
    dimensionValues = [],
    entityFilter = {},
    parameterValues = [],
    dimensionConditions,
    recordsData = [],
    seriesId,
    processFilters = [],
    dimensionAllValuesExcluded = []
}: Partial<GroupFilter> = {}): GroupFilter {
    return {
        groupName: groupName == null ? '' : groupName,
        dimensionValues: dimensionValues == null ? [] : dimensionValues,
        dimensionAllValuesExcluded: dimensionAllValuesExcluded == null ? [] : dimensionAllValuesExcluded,
        entityFilter: entityFilter == null ? {} : entityFilter,
        parameterValues: parameterValues == null ? [] : parameterValues,
        dimensionConditions: dimensionConditions,
        recordsData: recordsData == null ? [] : recordsData,
        processFilters: processFilters == null ? [] : processFilters,
        seriesId: seriesId
    } as any;
}
